import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import FramerNavBar from '../components/FramerNavBar';
import TeamMemberTextOnlyCard from '../components/TeamMemberTextOnlyCard';
import GoogleSignIn from '../components/GoogleSignIn';
import PrimaryButton from '../components/PrimaryButton';
import styles from './LandingPage.module.css';

function LandingPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);

  const handleGoogleSuccess = async (userInfo) => {
    try {
      console.log('Google sign-in successful, userInfo:', userInfo);
      await login(userInfo);
      console.log('Login successful, navigating to dashboard');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Google sign-in failed:', error);
      const errorMessage = error.message || 'Sign-in failed. Please try again.';
      alert(`Sign-in failed: ${errorMessage}`);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google sign-in error:', error);
    alert('Sign-in failed. Please try again.');
  };

  // If user is already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const faqs = [
    {
      question: "What makes 🌱 Kumu Connect different from other AI lesson tools?",
      answer: "Most AI tools are built for mainstream, high-resource contexts. 🌱 Kumu Connect is specifically designed to create culturally relevant lessons, align with standards, and support low-resource languages—ensuring that every classroom has access to meaningful curricula."
    },
    {
      question: "How was 🌱 Kumu Connect developed?",
      answer: "🌱 Kumu Connect was co-designed with Hawaiian language immersion (Kaiapuni) teachers through an iterative process of classroom feedback, prototyping, and refinement. Educators, technologists, and cultural experts shaped the system together to ensure trust, usability, and cultural accuracy."
    },
    {
      question: "Who decides what counts as \"culturally relevant\"?",
      answer: "Cultural relevance is guided by educators, cultural experts, and community partners. Our team uses co-design practices to ensure lesson content reflects community values and classroom realities, not outside assumptions."
    },
    {
      question: "How do you protect sacred and sensitive knowledge?",
      answer: "We safeguard knowledge by working with localized and restricted datasets, respecting community guidance on what should or should not be digitized and following CARE and FAIR data sovereignty principles. 🌱\n\nFurthermore, we are inspired by the Kiaʻi License and Kaitiakitanga License that go further in protecting community curated, often sacred data.\n\nKumu Connect is designed to support teachers while protecting cultural integrity, not to extract or commodify knowledge."
    },
    {
      question: "Can 🌱 Kumu Connect be modified for my community's language and culture?",
      answer: "Yes! 🌱 Kumu Connect was designed to grow with the communities it serves. We partner with educators and cultural practitioners, especially in Indigenous and low-resource language contexts, to co-create versions that honor each community's language, stories, and culture. If this resonates, please reach out! We'd love to be in partnership with you."
    }
  ];

  const teamMembers = [
    { name: "Josiah Hester", role: "Senior Project Contributor", linkedin: "https://www.linkedin.com/in/profjosiah/", image: "/assets/Headshots/josiah.jpg" },
    { name: "Rachel Baker-Ramos", role: "Product & Research Lead Contributor", linkedin: "https://www.linkedin.com/in/rachel-baker-ramos/", image: "/assets/Headshots/rachel.png" },
    { name: "Manas Mhasakar", role: "AI/ML Lead Contributor", linkedin: "https://www.linkedin.com/in/manasmhasakar/", image: "/assets/Headshots/manas.jpg" },
    { name: "Rebecca Diego", role: "Curriculum & Impact Lead Contributor", linkedin: "https://www.linkedin.com/in/rebecca-diego-15914b209/", image: "/assets/Headshots/Becky_Headshot.png" },
    { name: "Evyn-Bree Helekahi-Kaiwi", role: "Language & Graphic Design Contributor", linkedin: "https://www.linkedin.com/in/evyn-bree-helekahi-kaiwi-a88675382/", image: "/assets/Headshots/evyn.jpg" },
    { name: "Akanksha Tanwar", role: "Development Contributor", linkedin: "https://www.linkedin.com/in/akankshatanwar17/", image: "/assets/Headshots/akanksha.png"  },
    { name: "Alaka'i Iaea-Russell", role: "Curriculum & Language Contributor", image: "/assets/Headshots/ala.png" },
    { name: "Viswak Raja", role: "UX Design Contributor", linkedin: "https://www.linkedin.com/in/viswakraja/", image: "/assets/Headshots/viswak.jpg" },
    { name: "Ben Carter", role: "Student Research Contributor", linkedin: "https://www.linkedin.com/in/benjamin-d-carter/" },
    { name: "Will Gelder", role: "UX Research Contributor", linkedin: "https://www.linkedin.com/in/will-gelder-ms-hci-02920070/" },
    { name: "Jahnavi Kolakaluri", role: "UX Design Contributor", linkedin: "https://www.linkedin.com/in/jahnsite/" },
    { name: "Leah Cho", role: "UX Research Contributor", linkedin: "https://www.linkedin.com/in/leahcho22/" }
  ];

  return (
    <div className={styles.page}>
      <FramerNavBar
        onGoogleSuccess={handleGoogleSuccess}
        onGoogleError={handleGoogleError}
      />

      <main className={styles.main}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <div className={styles.logoSection}>
              <div className={styles.logoWrapper}>
                <img 
                  src="https://framerusercontent.com/images/8zeV89LOlB8u6tnebXEoCrGl598.png" 
                  alt="Kumu Connect Logo" 
                  className={styles.logo}
                />
              </div>
              <h1 className={styles.heroTitle}>
                Learning that Connects - To Culture, To Community, To You
              </h1>
            </div>
              <div className={styles.heroDescription}>
                <p className={styles.descriptionText}>
                  🌱 Kumu Connect is an innovative lesson plan generator that creates standards-aligned, culturally relevant STEM lessons, in English and ʻŌlelo Hawaiʻi—co-designed with Hawaiian immersion language teachers and built to expand access where mainstream tools fall short.
                </p>
                <div className={styles.heroSignUpCta}>
                  <div className={styles.signUpButtonShell}>
                    <PrimaryButton className={styles.signUpVisualButton} ariaLabel="Sign up with Google">
                      SIGN UP NOW!
                    </PrimaryButton>
                    <GoogleSignIn
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      size="large"
                      text="signup_with"
                      width={176}
                      className={styles.signUpGoogleOverlay}
                      containerStyle={{ minHeight: '46px', width: '100%' }}
                    />
                  </div>
                </div>
              </div>
          </div>

          {/* Video Section */}
          <div className={styles.videoSection}>
            <div className={styles.videoWrapper}>
              <iframe
                className={styles.video}
                src="https://www.youtube-nocookie.com/embed/r4VJ1IFW0I4?rel=0&modestbranding=1&playsinline=1"
                title="Kumu Connect"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        {/* Challenge Section */}
        <section id="solution" className={styles.challenge}>
          <h2 className={styles.sectionTitle}>The Challenge</h2>
          <div className={styles.challengeContent}>
            <div className={styles.challengeImage}>
              <img 
                src="/assets/HomePage-woman1/challenge_context.png"
                alt="Challenge context"
                loading="lazy"
              />
            </div>
            <div className={styles.challengeText}>
              <p className={styles.challengeParagraph}>
                Across the U.S., teacher shortages and limited resources leave schools struggling to provide relevant STEM curricula.
              </p>
              <p className={styles.challengeParagraph}>
                In Indigenous educational settings and low-resource languages, this gap is even greater: teachers are forced to create everything from scratch, while mainstream tools ignore cultural context.
              </p>
              <p className={styles.challengeParagraph}>
                The result is an inequitable system where whole communities are excluded from high-quality, culturally grounded education.
              </p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className={styles.features}>
          <div className={styles.featuresHeader}>
            <h2 className={styles.sectionTitle}>Our Solution</h2>
            <p className={styles.featuresSubtitle}>
              🌱 Kumu Connect transforms this landscape by:
            </p>
          </div>
          <div className={styles.featuresGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureCardInner}>
                <div className={styles.featureIcon}>
                  <img src="/assets/raphael_people.png" alt="" className={styles.featureIconImage} aria-hidden />
                </div>
                <div className={styles.featureContent}>
                  <h3 className={styles.featureTitle}>Delivering Culturally Relevant Content</h3>
                  <p className={styles.featureText}>
                    Lessons rooted in local stories, practices, and perspectives.
                  </p>
                </div>
              </div>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureCardInner}>
                <div className={styles.featureIcon}>
                  <img src="/assets/Frame_757.png" alt="" className={styles.featureIconImage} aria-hidden />
                </div>
                <div className={styles.featureContent}>
                  <h3 className={styles.featureTitle}>Ensuring Standards Alignment</h3>
                  <p className={styles.featureText}>
                    Built-in compliance with national and state standards.
                  </p>
                </div>
              </div>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureCardInner}>
                <div className={styles.featureIcon}>
                  <img src="/assets/cil_chat-bubble.png" alt="" className={styles.featureIconImage} aria-hidden />
                </div>
                <div className={styles.featureContent}>
                  <h3 className={styles.featureTitle}>Expanding Access</h3>
                  <p className={styles.featureText}>
                    Designed specifically for low-resource languages and under-resourced schools.
                  </p>
                </div>
              </div>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureCardInner}>
                <div className={styles.featureIcon}>
                  <img src="/assets/ph_butterfly.png" alt="" className={styles.featureIconImage} aria-hidden />
                </div>
                <div className={styles.featureContent}>
                  <h3 className={styles.featureTitle}>Scaling with Integrity</h3>
                  <p className={styles.featureText}>
                    Co-designed with teachers, tested in real classrooms, and built to adapt to diverse cultural contexts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className={styles.howItWorks}>
          <div className={styles.step}>
            <div className={styles.stepContent}>
              <h3 className={styles.stepTitle}>Define the Instructional Context</h3>
              <p className={styles.stepText}>
                Educator's begin by defining the core parameters of their lesson, including: Class Info, Grade Band, CSTA Concept, CSTA Standard, Language, Optional Add-ons
              </p>
            </div>
            <div className={styles.stepImage}>
              <img 
                src="https://framerusercontent.com/images/liix58S9x3bnHHTm5K1fTuK9JlQ.png" 
                alt="Define Instructional Context"
                loading="lazy"
              />
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepImage}>
              <img 
                src="https://framerusercontent.com/images/5YRc8Lgbq8aJykoB2XxU707d7U.png" 
                alt="Cultural Reference"
                loading="lazy"
              />
            </div>
            <div className={styles.stepContent}>
              <h3 className={styles.stepTitle}>Identify the Cultural Reference</h3>
              <p className={styles.stepText}>
                🌱 Kumu Connect generates three cultural references for the selected CS concept: a moʻolelo (story), ʻŌlelo Noʻeau (proverb) and a cultural practice.
              </p>
              <p className={styles.stepTextSmall}>
                Each reference has a "connection" description, which explains how the reference mirrors the CS concept.
              </p>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepContent}>
              <h3 className={styles.stepTitle}>Select Activities & Delivery Mode</h3>
              <p className={styles.stepText}>
                The user wraps up with a final "Activities" screen, which allows them to further define instruction mode, such as "outdoors" or "low-tech."
              </p>
            </div>
            <div className={styles.stepImage}>
              <img 
                src="https://framerusercontent.com/images/nCx8ujKYKYa4oPfTNjU77rHFuIM.png" 
                alt="Select Activities"
                loading="lazy"
              />
            </div>
          </div>
          <div className={styles.generationStep}>
            <div className={styles.generationIcon}>
              <img 
                src="https://framerusercontent.com/images/aqirfzUi6pP3N4zRoQVRzLO2So.gif" 
                alt="Loading"
                loading="lazy"
              />
            </div>
            <h3 className={styles.stepTitle}>Lesson Generation</h3>
            <p className={styles.stepText}>
              🌱 Kumu Connect then generates the lesson!
            </p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepImage}>
              <img 
                src="https://framerusercontent.com/images/xRM08481Z7U5fzEq5FRjhQy5R90.png" 
                alt="Refine Lesson"
                loading="lazy"
              />
            </div>
            <div className={styles.stepContent}>
              <h3 className={styles.stepTitle}>Refine and Finalize the Lesson</h3>
              <p className={styles.stepText}>
                The user's selections are assembled by 🌱 Kumu Connect into a complete lesson plan containing: Learning objectives, Activities, Discussion prompts, and Assessments. On this editor screen, educators make final modifications before saving or exporting their lesson!
              </p>
            </div>
          </div>
        </section>

        {/* About Us Section */}
        <section id="about" className={styles.about}>
          <h2 className={styles.aboutTitle}>About Us</h2>
          <div className={styles.mission}>
            <h3 className={styles.missionTitle}>Our Mission</h3>
            <div className={styles.missionContent}>
              <p className={styles.missionTextHawaiian}>
                I mea e naʻauao ai kā kākou mau keiki, nā kaiāulu o Hawaiʻi, he koʻikoʻi ke aʻoaʻo a hoʻonaʻauao aku iā lākou ma luna o nā ʻike waiwai… ʻO ia hoʻi ka nuʻukia o Kumu Connect.
              </p>
              <p className={styles.missionText}>
                We envision a world where education nurtures belonging—to culture, to community, and to the future—so every child can thrive.
              </p>
            </div>
          </div>
          
          {/* Team Section */}
          <div className={styles.teamSection}>
            <h3 className={styles.teamTitle}>Our Team</h3>
            <p className={styles.teamDescription}>
              🌱 Kumu Connect is a collaborative project. Our Kanaka Maoli-led team is made up of researchers, technologists, educators, and cultural specialists from the Ka Moamoa Lab and Ulu Lāhui Foundation. We bring deep experience in Hawaiian education, Indigenous technology development, Indigenous data sovereignty, AI for "low-resource" languages, and culturally-revitalizing educational technology. As a small team wearing many hats, we're building the infrastructure, partnerships, and pathways that will carry Kumu Connect from idea to impact. Our current and past contributors include:
            </p>
            <div className={styles.teamGrid}>
              {teamMembers.map((member, index) =>
                member.image ? (
                  <div key={index} className={styles.teamCard}>
                    <div className={styles.teamCardInner}>
                      <div className={styles.teamImage}>
                        <img
                          src={member.image}
                          alt={`${member.name} headshot`}
                          className={styles.teamHeadshot}
                          loading="lazy"
                        />
                      </div>
                      <div className={styles.teamInfo}>
                        <h4 className={styles.teamName}>{member.name}</h4>
                        <p className={styles.teamRole}>{member.role}</p>
                        {member.linkedin && (
                          <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className={styles.teamLink}>
                            LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <TeamMemberTextOnlyCard
                    key={index}
                    name={member.name}
                    role={member.role}
                    linkedin={member.linkedin}
                  />
                )
              )}
            </div>
          </div>
        </section>

        {/* FAQs Section */}
        <section id="faq" className={styles.faqs}>
          <h2 className={styles.faqsTitle}>F.A.Q.s</h2>
          <div className={styles.faqsContainer}>
            {faqs.map((faq, index) => (
              <div key={index} className={styles.faqItem}>
                <button 
                  className={styles.faqQuestion}
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className={styles.faqIcon}>{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className={styles.faqAnswer}>
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className={styles.contact}>
          <h2 className={styles.contactTitle}>Get in Touch</h2>
          <p className={styles.contactSubtitle}>Email us directly and we will get back to you.</p>
          <a href="mailto:kumuconnectai@gmail.com" className={styles.contactEmailLink} aria-label="Email Kumu Connect">
            <svg className={styles.contactMailIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 6h16v12H4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className={styles.contactEmailText}>kumuconnectai@gmail.com</span>
          </a>
        </section>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerSection}>
            <div className={styles.footerLogo}>
              <img 
                src="https://framerusercontent.com/images/cfeJIebmkwxeQqW9MxhodgIk8IM.png" 
                alt="Kumu Connect" 
                className={styles.footerLogoImg}
              />
            </div>
            <div className={styles.supportedBy}>
              <h6 className={styles.footerHeading}>Supported by:</h6>
              <img
                className={styles.supporterArt}
                src="https://framerusercontent.com/images/JGHhfT4mRtRdS40eSFSKXPFojvw.png?width=1186&height=379"
                alt="Google.org AI support"
                loading="lazy"
              />
            </div>
          </div>
          <div className={styles.footerSection}>
            <h6 className={styles.footerHeading}>Socials</h6>
            <ul className={styles.footerList}>
              <li>
                <a className={styles.footerLink} href="https://www.linkedin.com/company/kumu-connect/" target="_blank" rel="noopener noreferrer">
                  LinkedIn
                </a>
              </li>
              <li>
                <a className={styles.footerLink} href="https://www.instagram.com/kumu.connect/" target="_blank" rel="noopener noreferrer">
                  Instagram
                </a>
              </li>
            </ul>
          </div>
          <div className={styles.footerSection}>
            <h6 className={styles.footerHeading}>The Collaboration</h6>
            <ul className={styles.footerList}>
              <li>
                <a className={styles.footerLink} href="https://kamoamoa.com/" target="_blank" rel="noopener noreferrer">
                  Ka Moamoa Lab
                </a>
              </li>
              <li>
                <a className={styles.footerLink} href="https://ululahui.org/" target="_blank" rel="noopener noreferrer">
                  Ulu Lāhui Foundation
                </a>
              </li>
              <li>
                <a className={styles.footerLink} href="https://www.places-education.com/" target="_blank" rel="noopener noreferrer">
                  PLACES Education
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <span className={styles.footerText}>© {new Date().getFullYear()} Kumu Connect</span>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
